// @ts-nocheck -- Shopify theme asset; runtime behavior is covered by tests/locale-guard.test.mjs.
(() => {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__bcgiftCnLocaleGuardLoaded) return;
  window.__bcgiftCnLocaleGuardLoaded = true;

  const ROOT_SELECTOR = '[data-bcgift-cn-locale-guard]';
  const FORM_ID = 'BcgiftCnLocaleForm';
  const MARKER_PARAM = 'bcgift_locale';
  const DEBUG_PARAM = 'bcgift_locale_debug';
  const EXPLICIT_CHOICE_KEY = 'bcgiftLocaleExplicitChoice';
  const SESSION_PREFERENCE_KEY = 'bcgiftPreferredLocale';
  const ATTEMPT_PREFIX = 'bcgiftLocaleGuardAttempted:';
  const LAST_DECISION_KEY = 'bcgiftLocaleGuardLastDecision';
  const DISCOVERY_TIMEOUT_MS = 1200;
  const ATTEMPT_WINDOW_MS = 15000;
  const MAX_ATTEMPTS_PER_WINDOW = 2;

  function safeGet(storage, key) {
    try {
      return storage.getItem(key) || '';
    } catch (_error) {
      return '';
    }
  }

  function safeSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (_error) {
      // Storage can be unavailable in private browsing. Locale switching still works.
    }
  }

  function safeRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (_error) {
      // Storage can be unavailable in private browsing.
    }
  }

  function normalizeLocale(value) {
    return String(value || '').trim().replace('_', '-').toLowerCase();
  }

  function isChineseLocale(value) {
    return normalizeLocale(value).startsWith('zh');
  }

  function isEnglishLocale(value) {
    return normalizeLocale(value).startsWith('en');
  }

  function browserPrefersChinese() {
    const languages = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language || navigator.userLanguage || ''];
    return languages.some(isChineseLocale);
  }

  function isWeChat() {
    return /MicroMessenger|WeChat|Weixin|wxwork/i.test(navigator.userAgent || '');
  }

  function markerRequestsChinese(url) {
    return isChineseLocale(url.searchParams.get(MARKER_PARAM));
  }

  function cleanUrl(url) {
    const cleaned = new URL(url.toString());
    cleaned.searchParams.delete(MARKER_PARAM);
    return cleaned;
  }

  function buildReturnTo(url) {
    const cleaned = cleanUrl(url);
    const pathname = cleaned.pathname.replace(/^\/(?:zh(?:-cn|-hans)?)(?=\/|$)/i, '') || '/';
    return pathname + cleaned.search + cleaned.hash;
  }

  function renderDebugStatus(details) {
    const url = new URL(window.location.href);
    if (url.searchParams.get(DEBUG_PARAM) !== '1') return;
    let panel = document.querySelector('[data-bcgift-cn-debug-panel]');
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute('data-bcgift-cn-debug-panel', '');
      Object.assign(panel.style, {
        position: 'fixed',
        left: '8px',
        bottom: '8px',
        zIndex: '2147483647',
        maxWidth: 'calc(100vw - 16px)',
        padding: '8px 10px',
        borderRadius: '6px',
        background: '#111827',
        color: '#fff',
        font: '12px/1.4 -apple-system, BlinkMacSystemFont, sans-serif',
        boxShadow: '0 4px 18px rgba(0,0,0,.25)',
      });
      document.body.appendChild(panel);
    }
    panel.textContent = `CN Preview A+B · ${details.action} · ${details.reason} · ${details.currentLocale || ''}`;
  }

  function recordDecision(details) {
    safeSet(window.sessionStorage, LAST_DECISION_KEY, JSON.stringify(details));
    if (new URL(window.location.href).searchParams.get(DEBUG_PARAM) === '1') {
      console.info('[BCgift locale guard]', details);
      renderDebugStatus(details);
    }
  }

  function normalizedEnglishPath(pathname) {
    return pathname.replace(/^\/(?:zh(?:-cn|-hans)?)(?=\/|$)/i, '') || '/';
  }

  function readAttemptState(storage, key) {
    const raw = safeGet(storage, key);
    if (!raw) return { count: 0, lastAttemptAt: 0 };
    try {
      const state = JSON.parse(raw);
      return {
        count: Number(state?.count || 0),
        lastAttemptAt: Number(state?.lastAttemptAt || 0),
      };
    } catch (_error) {
      return { count: 1, lastAttemptAt: 0 };
    }
  }

  function showManualChineseSwitch(root, url) {
    const button = root.querySelector('[data-bcgift-cn-manual-switch]');
    if (!button) return;
    root.hidden = false;
    button.hidden = false;
    Object.assign(root.style, {
      position: 'fixed',
      right: '10px',
      bottom: '10px',
      zIndex: '2147483646',
    });
    if (button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => {
      safeRemove(window.sessionStorage, ATTEMPT_PREFIX + normalizedEnglishPath(url.pathname));
      submitChineseLocalization(root, new URL(window.location.href), 'manual-cn-button');
    });
  }

  function attachManualLanguageChoiceListener() {
    const persistChoice = (value) => {
      if (isChineseLocale(value)) {
        safeSet(window.localStorage, EXPLICIT_CHOICE_KEY, 'zh');
      } else if (isEnglishLocale(value)) {
        safeSet(window.localStorage, EXPLICIT_CHOICE_KEY, 'en');
      }
    };

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input?.matches?.('[name="language_code"]')) return;
      persistChoice(input.value);
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id === FORM_ID) return;
      const languageInput = form.querySelector('[name="language_code"]');
      if (!languageInput) return;
      persistChoice(languageInput.value);
    }, true);
  }

  async function discoverSuggestedLocale(rootUrl) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => controller?.abort(), DISCOVERY_TIMEOUT_MS);

    try {
      const basePath = String(rootUrl || '/').replace(/\/?$/, '/');
      const endpoint = new URL(`${basePath}browsing_context_suggestions.json`, window.location.origin);
      endpoint.searchParams.set('country[enabled]', 'true');
      endpoint.searchParams.set('language[enabled]', 'true');

      const response = await fetch(endpoint.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller?.signal,
      });
      if (!response.ok) return { country: '', language: '' };

      const payload = await response.json().catch(() => ({}));
      const suggestion = Array.isArray(payload?.suggestions)
        ? payload.suggestions[0]
        : payload?.suggestions;
      const suggestionParts = suggestion?.parts || {};
      const detectedCountry = payload?.detected_values?.country?.handle || '';
      const suggestedCountry = suggestionParts?.country?.handle || '';
      const detectedLanguage = payload?.detected_values?.language?.handle || '';
      const suggestedLanguage = suggestionParts?.language?.handle || '';

      return {
        country: String(detectedCountry || suggestedCountry || '').toUpperCase(),
        language: String(detectedLanguage || suggestedLanguage || ''),
      };
    } catch (_error) {
      return { country: '', language: '' };
    } finally {
      window.clearTimeout(timer);
    }
  }

  function submitChineseLocalization(root, url, reason) {
    const form = document.getElementById(FORM_ID);
    const returnTo = form?.querySelector('[name="return_to"]');
    if (!form || !returnTo) return false;

    const attemptKey = ATTEMPT_PREFIX + normalizedEnglishPath(url.pathname);
    const now = Date.now();
    let attemptState = readAttemptState(window.sessionStorage, attemptKey);
    if (now - attemptState.lastAttemptAt > ATTEMPT_WINDOW_MS) {
      attemptState = { count: 0, lastAttemptAt: 0 };
    }
    if (attemptState.count >= MAX_ATTEMPTS_PER_WINDOW) {
      recordDecision({ action: 'stop', reason: 'loop-prevention', currentLocale: root.dataset.currentLocale });
      showManualChineseSwitch(root, url);
      return false;
    }

    safeSet(window.sessionStorage, attemptKey, JSON.stringify({
      count: attemptState.count + 1,
      lastAttemptAt: now,
    }));
    safeSet(window.sessionStorage, SESSION_PREFERENCE_KEY, 'zh');
    returnTo.value = buildReturnTo(url);
    recordDecision({
      action: 'switch-to-zh',
      reason,
      currentLocale: root.dataset.currentLocale,
      returnTo: returnTo.value,
    });

    form.submit();
    return true;
  }

  async function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || root.dataset.designMode === 'true') return;

    attachManualLanguageChoiceListener();

    const url = new URL(window.location.href);
    const currentLocale = root.dataset.currentLocale || document.documentElement.lang || '';
    const hasChineseMarker = markerRequestsChinese(url);
    const explicitChoice = safeGet(window.localStorage, EXPLICIT_CHOICE_KEY);
    const sessionPreference = safeGet(window.sessionStorage, SESSION_PREFERENCE_KEY);

    if (isChineseLocale(currentLocale)) {
      safeSet(window.sessionStorage, SESSION_PREFERENCE_KEY, 'zh');
      safeRemove(window.sessionStorage, ATTEMPT_PREFIX + normalizedEnglishPath(url.pathname));
      if (url.searchParams.has(MARKER_PARAM)) {
        const cleaned = cleanUrl(url);
        window.history.replaceState({}, '', cleaned.pathname + cleaned.search + cleaned.hash);
      }
      recordDecision({ action: 'stay-zh', reason: 'already-zh', currentLocale });
      return;
    }

    if (!hasChineseMarker && explicitChoice === 'en') {
      recordDecision({ action: 'stay-en', reason: 'explicit-en', currentLocale });
      return;
    }

    if (hasChineseMarker) {
      submitChineseLocalization(root, url, 'explicit-cn-link');
      return;
    }

    if (explicitChoice === 'zh' || sessionPreference === 'zh') {
      submitChineseLocalization(root, url, explicitChoice === 'zh' ? 'explicit-zh' : 'session-zh');
      return;
    }

    if (isWeChat()) {
      submitChineseLocalization(root, url, 'wechat');
      return;
    }

    if (browserPrefersChinese()) {
      submitChineseLocalization(root, url, 'browser-language');
      return;
    }

    const suggestion = await discoverSuggestedLocale(root.dataset.rootUrl || '/');
    if (isChineseLocale(suggestion.language)) {
      submitChineseLocalization(root, url, 'shopify-language-suggestion');
      return;
    }
    if (suggestion.country === 'CN') {
      submitChineseLocalization(root, url, 'shopify-country-cn');
      return;
    }

    recordDecision({
      action: 'stay-en',
      reason: 'no-cn-signal',
      currentLocale,
      detectedCountry: suggestion.country,
      detectedLanguage: suggestion.language,
    });
  }

  // The snippet and form are immediately before this asset, so initialize now.
  // This avoids waiting for DOMContentLoaded in slower mobile in-app browsers.
  init();
})();
